import { useGtm } from '@gtm-support/vue-gtm'
import { ProjectState } from '@/store/project-store'
import { detect } from 'detect-browser'
import { Mp4WasmEncoderConfig } from '@/pixi/types'

export default class GtmHelper {
  private static instance: GtmHelper
  protected gtm = useGtm()
  protected readonly EVENT_NAME = 'Visualizer'

  private constructor () {
    //
  }

  public static getInstance (): GtmHelper {
    if (!GtmHelper.instance) {
      GtmHelper.instance = new GtmHelper()
    }

    return GtmHelper.instance
  }

  public isEnabled (): boolean {
    return this.gtm?.enabled() ?? false
  }

  public enable (): void {
    this.gtm?.enable(true)
  }

  public disable (): void {
    this.gtm?.enable(false)
  }

  public triggerAudioFileUploadGtmEvents (duration: number, fileSize: number): void {
    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Import Track',
      label: 'Song Length (s)',
      value: Math.ceil(duration)
    })

    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Import Track',
      label: 'Song Size (mb)',
      value: this.bytesToMbs(fileSize)
    })
  }

  public triggerBackgroundUploadGtmEvent (fileSize: number): void {
    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Visualizer Use',
      label: 'User Uploaded Background Image (mb)',
      value: this.bytesToMbs(fileSize)
    })
  }

  public triggerLogoUploadGtmEvent (fileSize: number): void {
    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Visualizer Use',
      label: 'User Uploaded Logo and Sticker (mb)',
      value: this.bytesToMbs(fileSize)
    })
  }

  public triggerBackgroundSearchEvent (searchString: string): void {
    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Visualizer Use',
      label: 'Background Search Term',
      value: searchString
    })
  }

  public triggerExportGtmEvents (projectStore: ProjectState, audioDuration: number): void {

    const backgroundName = projectStore.background.backgroundFileName !== null 
      ? 'User uploaded ' + projectStore.background.backgroundFileName 
      : projectStore.background.backgroundUrl

    projectStore.stickers.forEach(sticker => {
      this.gtm?.trackEvent({
        event: this.EVENT_NAME,
        category: 'Exported Visualizer',
        label: 'Exported Sticker',
        value: sticker.url
      })
    })

    projectStore.logoStickers.forEach(logo => {
      this.gtm?.trackEvent({
        event: this.EVENT_NAME,
        category: 'Exported Visualizer',
        label: 'Exported Logo',
        value: 'User uploaded ' + logo.filename
      })
    })

    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Exported Visualizer',
      label: 'Exported Background Image',
      value: backgroundName
    })

    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Exported Visualizer',
      label: 'Number of exported text boxes',
      value: projectStore.textStates.length
    })

    projectStore.activeEffectStates?.forEach(effect => {
      this.gtm?.trackEvent({
        event: this.EVENT_NAME,
        category: 'Exported Visualizer',
        label: 'Exported Effect',
        value: effect.name
      })
    })
    if (projectStore.activeEffectStates?.size === 0) {
      this.gtm?.trackEvent({
        event: this.EVENT_NAME,
        category: 'Exported Visualizer',
        label: 'Exported Effect',
        value: 'None'
      })
    }

    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Exported Visualizer',
      label: 'Exported Orientation',
      value: projectStore.orientation
    })

    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Exported Visualizer',
      label: 'Exported Song Length (s)',
      value: Math.ceil(audioDuration)
    })
  }

  public triggerDownloadGtmEvents (fileSize: number, exportDuration: number, config: Mp4WasmEncoderConfig): void {
    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Exported Visualizer',
      label: 'Export Success'
    })

    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Exported Visualizer',
      label: 'Exported Video Size (mb)',
      value: this.bytesToMbs(fileSize)
    })

    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Exported Visualizer',
      label: 'Export Time Duration (s)',
      value: Math.ceil(exportDuration / 1000)
    })

    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Exported Visualizer',
      label: 'Export Encoder Config',
      value: JSON.stringify(config)
    })
  }

  public triggerUnsupportedBrowserEvent (): void {
    const browser = detect()
    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Unsupported Browser',
      label: browser?.os + ', ' + browser?.name,
      value: browser?.version
    })
  }

  public triggerNotEnoughMemoryEvent (): void {
    const browser = detect()
    this.gtm?.trackEvent({
      event: this.EVENT_NAME,
      category: 'Not Enough Memory',
      label: browser?.os + ', ' + browser?.name,
      value: browser?.version
    })
  }

  protected bytesToMbs (bytes: number): number {
    return Number((bytes / 1000000).toFixed(3))
  }
}
